import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Power, Calendar, Clock, Shield, Save, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const AppControlTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    app_active: true,
    start_date: '',
    start_time: '08:00',
    end_date: '',
    end_time: '16:00',
    inactive_message: "L'application est temporairement fermée. Veuillez réessayer plus tard.",
  });
  const [liveStatus, setLiveStatus] = useState(null);

  const parseScheduleDates = (data) => {
    let start_date = '', start_time = '08:00', end_date = '', end_time = '16:00';
    if (data.scheduled_start) {
      const d = new Date(data.scheduled_start);
      start_date = d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
      start_time = d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto' });
    }
    if (data.scheduled_end) {
      const d = new Date(data.scheduled_end);
      end_date = d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
      end_time = d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto' });
    }
    return { start_date, start_time, end_date, end_time };
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResp, statusResp] = await Promise.all([
        axios.get(`${API}/admin/app-settings`),
        axios.get(`${API}/app-status`),
      ]);
      const data = settingsResp.data;
      const dates = parseScheduleDates(data);
      setForm({
        app_active: data.app_active !== false,
        ...dates,
        inactive_message: data.inactive_message || "L'application est temporairement fermée. Veuillez réessayer plus tard.",
      });
      setLiveStatus(statusResp.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const toggleApp = async () => {
    const newState = !form.app_active;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/app-settings`, { app_active: newState });
      setForm(f => ({ ...f, app_active: newState }));
      // Refresh live status
      const resp = await axios.get(`${API}/app-status`);
      setLiveStatus(resp.data);
      toast.success(newState ? 'Application ACTIVÉE' : 'Application DÉSACTIVÉE');
    } catch {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      let scheduled_start = null;
      let scheduled_end = null;
      if (form.start_date && form.end_date) {
        scheduled_start = new Date(`${form.start_date}T${form.start_time}:00`).toISOString();
        scheduled_end = new Date(`${form.end_date}T${form.end_time}:00`).toISOString();
      }
      await axios.put(`${API}/admin/app-settings`, {
        app_active: form.app_active,
        scheduled_start,
        scheduled_end,
        inactive_message: form.inactive_message,
      });
      const resp = await axios.get(`${API}/app-status`);
      setLiveStatus(resp.data);
      toast.success('Paramètres sauvegardés');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const clearSchedule = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/app-settings`, {
        app_active: form.app_active,
        scheduled_start: null,
        scheduled_end: null,
        inactive_message: form.inactive_message,
      });
      setForm(f => ({ ...f, start_date: '', start_time: '08:00', end_date: '', end_time: '16:00' }));
      const resp = await axios.get(`${API}/app-status`);
      setLiveStatus(resp.data);
      toast.success('Plage horaire supprimée — mode manuel actif');
    } catch {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="animate-pulse text-lg" style={{ color: '#2E6CF5' }}>Chargement...</div></div>;
  }

  const isActive = liveStatus?.active !== false;
  const hasSchedule = !!(form.start_date && form.end_date);
  const mode = hasSchedule && form.app_active ? 'schedule' : 'manual';

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#002768' }} data-testid="app-control-title">
        Contrôle de l'application
      </h2>

      {/* Mode indicator */}
      <div className="mb-4 p-3 rounded-xl flex items-center gap-3" style={{ backgroundColor: mode === 'schedule' ? '#EFF6FF' : '#F0FDF4', borderLeft: `4px solid ${mode === 'schedule' ? '#2E6CF5' : '#24B219'}` }}>
        <Info className="w-5 h-5 flex-shrink-0" style={{ color: mode === 'schedule' ? '#2E6CF5' : '#24B219' }} />
        <div className="text-sm">
          {mode === 'schedule' ? (
            <span><strong style={{ color: '#2E6CF5' }}>Mode planifié</strong> — L'app suit la plage horaire. Pour revenir au mode manuel, supprimez la plage horaire.</span>
          ) : (
            <span><strong style={{ color: '#24B219' }}>Mode manuel</strong> — Le bouton ON/OFF contrôle l'app. Configurez une plage horaire pour passer en mode planifié.</span>
          )}
        </div>
      </div>

      {/* Main Status + Toggle */}
      <Card className="glass border-0 mb-6" style={{ borderLeft: `4px solid ${isActive ? '#24B219' : '#EE2130'}` }}>
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: isActive ? '#E0F8E0' : '#FEE2E2' }}>
                <Power className="w-8 h-8" style={{ color: isActive ? '#24B219' : '#EE2130' }} />
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ color: isActive ? '#24B219' : '#EE2130' }} data-testid="app-status-text">
                  {isActive ? 'Application ACTIVE' : 'Application FERMÉE'}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {!form.app_active && "Désactivée manuellement par l'instructeur"}
                  {form.app_active && !isActive && liveStatus?.reason === 'schedule' && "En dehors de la plage horaire planifiée"}
                  {isActive && !hasSchedule && "Mode manuel — Les étudiants ont accès"}
                  {isActive && hasSchedule && "Mode planifié — Dans la plage horaire autorisée"}
                </p>
              </div>
            </div>
            <button
              onClick={toggleApp}
              disabled={saving}
              className="flex items-center gap-3 px-8 py-4 rounded-2xl text-white font-bold text-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              style={{ backgroundColor: form.app_active ? '#EE2130' : '#24B219' }}
              data-testid="app-toggle-btn"
            >
              <Power className="w-6 h-6" />
              {form.app_active ? 'DÉSACTIVER' : 'ACTIVER'}
            </button>
          </div>
          {!form.app_active && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">
                L'application est désactivée. Les simulations, variantes RAO et rétroactions sont bloquées.
              </p>
            </div>
          )}
          {form.app_active && !isActive && liveStatus?.reason === 'schedule' && (
            <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                L'app est activée mais la plage horaire est terminée ou pas encore commencée. Les étudiants voient le message d'indisponibilité. <strong>Supprimez la plage horaire</strong> pour passer en mode manuel et rendre l'app disponible immédiatement.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schedule */}
        <Card className="glass border-0" style={hasSchedule ? { borderTop: '3px solid #2E6CF5' } : {}}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5" style={{ color: '#2E6CF5' }} />
              <h3 className="text-lg font-bold" style={{ color: '#002768' }}>Plage horaire</h3>
              {hasSchedule && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: '#2E6CF5' }}>Active</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {hasSchedule
                ? "L'application est accessible uniquement durant cette période. Supprimez la plage pour revenir au mode manuel."
                : "Configurez une plage pour limiter l'accès à une période précise (ex: pendant votre cours)."}
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="schedule-start-date" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Heure début</label>
                  <input type="time" value={form.start_time} onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="schedule-start-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
                  <input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="schedule-end-date" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Heure fin</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="schedule-end-time" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={saveSchedule} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#2E6CF5' }} data-testid="save-schedule-btn">
                  <Save className="w-4 h-4" /> Sauvegarder
                </button>
                {hasSchedule && (
                  <button onClick={clearSchedule} disabled={saving} className="px-4 py-2 rounded-lg font-medium text-sm hover:bg-red-100" style={{ color: '#EE2130' }} data-testid="clear-schedule-btn">
                    Supprimer la plage
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Message */}
        <Card className="glass border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5" style={{ color: '#F4621C' }} />
              <h3 className="text-lg font-bold" style={{ color: '#002768' }}>Message d'indisponibilité</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Affiché aux étudiants lorsque l'application est fermée.
            </p>
            <textarea
              value={form.inactive_message}
              onChange={(e) => setForm(f => ({ ...f, inactive_message: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
              data-testid="inactive-message"
            />
            <div className="mt-3 p-3 rounded-xl bg-gray-100 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-1">Aperçu :</p>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700">{form.inactive_message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#2E6CF5' }} />
          <div className="text-sm" style={{ color: '#002768' }}>
            <p className="font-medium mb-1">Comment ça fonctionne :</p>
            <ul className="space-y-1 text-gray-600 list-disc pl-4">
              <li><strong>Mode manuel</strong> : le bouton ON/OFF contrôle l'accès instantanément</li>
              <li><strong>Mode planifié</strong> : configurez une plage horaire et l'app sera accessible uniquement durant cette période</li>
              <li>Pour <strong>revenir au mode manuel</strong>, supprimez la plage horaire</li>
              <li>Les appels LLM (simulations, variantes RAO, rétroactions) sont <strong>bloqués</strong> quand l'app est fermée</li>
              <li>Les <strong>administrateurs</strong> gardent toujours un accès complet</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppControlTab;
